import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Typography, Paper, Box, Grid, Divider, Button,
  FormControlLabel, Checkbox, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Alert, Card, CardContent, Chip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import api from '../utils/api';
import ReturnStages from './ReturnStages';

const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('fi-FI');
};

const getStatusChip = (status) => {
  let color = 'default';
  let label = status || 'Tuntematon';
  
  switch(status) {
    case 'pending':
      color = 'warning';
      label = 'Kesken';
      break;
    case 'completed':
      color = 'success';
      label = 'Valmis';
      break;
    case 'rejected':
      color = 'error';
      label = 'Hylätty';
      break;
    default:
      break;
  }
  
  return <Chip label={label} color={color} size="small" />;
};

const ReturnDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [returnData, setReturnData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // State for payment update dialog
  const [openPaymentDialog, setOpenPaymentDialog] = useState(false);
  const [paymentRefunded, setPaymentRefunded] = useState(false);
  const [updatingPayment, setUpdatingPayment] = useState(false);

  useEffect(() => {
    fetchReturnData();
  }, [id]);

  const fetchReturnData = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/returns/${id}`);
      setReturnData(response.data);
      setPaymentRefunded(response.data.payment_refunded);
    } catch (err) {
      console.error('Error fetching return data:', err);
      setError('Virhe palautuksen tietojen haussa. Yritä myöhemmin uudelleen.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenPaymentDialog = () => {
    setOpenPaymentDialog(true);
  };

  const handleClosePaymentDialog = () => {
    setOpenPaymentDialog(false);
  };

  const handleUpdatePayment = async () => {
    try {
      setUpdatingPayment(true);
      
      // Only update the payment_refunded field
      await api.put(`/api/returns/${id}`, {
        ...returnData,
        payment_refunded: paymentRefunded
      });
      
      setSuccess('Maksun tila päivitetty onnistuneesti!');
      setReturnData(prev => ({
        ...prev,
        payment_refunded: paymentRefunded
      }));
      
      handleClosePaymentDialog();
      
      // Clear success message after a delay
      setTimeout(() => {
        setSuccess(null);
      }, 3000);
      
    } catch (err) {
      console.error('Error updating payment status:', err);
      setError('Virhe maksun tilan päivityksessä. Yritä myöhemmin uudelleen.');
    } finally {
      setUpdatingPayment(false);
    }
  };

  const handleBackToList = () => {
    navigate('/returns');
  };

  const handleEditReturn = () => {
    navigate(`/returns/edit/${id}`);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!returnData) {
    return (
      <Alert severity="warning" sx={{ mt: 2 }}>
        Palautusta ei löytynyt.
      </Alert>
    );
  }

  return (
    <div>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Button 
          startIcon={<ArrowBackIcon />} 
          onClick={handleBackToList}
          variant="outlined"
        >
          Takaisin
        </Button>
        <Typography variant="h4" component="h1">
          Palautuksen tiedot
        </Typography>
        <Button 
          startIcon={<EditIcon />} 
          onClick={handleEditReturn}
          variant="contained" 
          color="primary"
        >
          Muokkaa
        </Button>
      </Box>

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6" gutterBottom>
                Perustiedot
              </Typography>
              {getStatusChip(returnData.status)}
            </Box>
            <Divider sx={{ mb: 2 }} />
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary">Asiakas</Typography>
            <Typography variant="body1">{returnData.customer_name || '-'}</Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary">Myyjä</Typography>
            <Typography variant="body1">{returnData.seller_name || '-'}</Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary">Tilausnumero</Typography>
            <Typography variant="body1">{returnData.order_number || '-'}</Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary">Laskunumero</Typography>
            <Typography variant="body1">{returnData.invoice_number || '-'}</Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary">Ilmoitus palautuksesta pvm</Typography>
            <Typography variant="body1">{formatDate(returnData.notification_date)}</Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary">Tuote palautunut pvm</Typography>
            <Typography variant="body1">{formatDate(returnData.return_date)}</Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary">Mitä palautetaan</Typography>
            <Typography variant="body1">{returnData.items_to_return || '-'}</Typography>
          </Grid>

          <Grid item xs={12}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Maksu palautettu</Typography>
                <Typography variant="body1">
                  {returnData.payment_refunded ? 'Kyllä' : 'Ei'}
                </Typography>
              </Box>
              <Button 
                variant="outlined" 
                color="primary"
                onClick={handleOpenPaymentDialog}
              >
                Päivitä maksun tila
              </Button>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary">Tilinumero</Typography>
            <Typography variant="body1" sx={{ 
              bgcolor: returnData.account_number ? 'rgba(255, 235, 59, 0.2)' : 'inherit',
              p: returnData.account_number ? 1 : 0,
              borderRadius: 1
            }}>
              {returnData.account_number || '-'}
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary">Tiedot</Typography>
            <Typography variant="body1">{returnData.information || '-'}</Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary">Lisätiedot</Typography>
            <Typography variant="body1">{returnData.additional_info || '-'}</Typography>
          </Grid>
        </Grid>
      </Paper>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Palautuksen vaiheet
          </Typography>
          <ReturnStages returnId={id} />
        </CardContent>
      </Card>

      {/* Payment Update Dialog */}
      <Dialog open={openPaymentDialog} onClose={handleClosePaymentDialog}>
        <DialogTitle>Päivitä maksun tila</DialogTitle>
        <DialogContent>
          <FormControlLabel
            control={
              <Checkbox
                checked={paymentRefunded}
                onChange={(e) => setPaymentRefunded(e.target.checked)}
                name="paymentRefunded"
                color="primary"
              />
            }
            label="Maksu palautettu"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePaymentDialog} color="inherit">
            Peruuta
          </Button>
          <Button 
            onClick={handleUpdatePayment} 
            color="primary" 
            variant="contained"
            disabled={updatingPayment}
          >
            {updatingPayment ? <CircularProgress size={24} /> : 'Tallenna'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ReturnDetail;
